'use client';

import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { Alert, Box, Image, Spacer, SpacerSize, Title, TitleLevels, TitleMarkup } from '@bytel/trilogy-react-ts';

import { FaiProduct, RangesNewGeneration } from '@app-types/fai';
import { JOURNEYS, RepeaterProduct } from '@app-types/product';

import { useHome } from '@landing/hooks';

import { BAndyouPrice } from '../plan-header/b-and-you-price';
import BboxCard from './bbox-card';
import BboxOtisOption from './bbox-otis-option';
import BboxOtisOptionModal from './bbox-otis-option-modal';
import FaiDetailsModal from './modal-otis';
import OfferRadioList from './offer-radio-list';

function getPlanPrice(product?: FaiProduct) {
    if (!product) {
        return 0;
    }

    return product.product.price + product.product.rental_fees;
}

interface BoxConfiguratorProps {
    faiProducts: FaiProduct[];
    repeaters?: RepeaterProduct[];
}

export function BboxConfigurator({ faiProducts, repeaters }: Readonly<BoxConfiguratorProps>) {
    const [detailModalOffer, setDetailModalOffer] = useState<RangesNewGeneration | undefined>();
    const [isOptionModalOpened, setIsOptionModalOpened] = useState(false);
    const [shouldShowAlert, setShouldShowAlert] = useState(false);

    const {
        setSelectedFaiOption,
        selectedFaiOption,
        selectedFaiProduct: _selectedFaiProduct,
        setSelectedFaiProduct,
        temporarilySelectedFaiProduct: _temporarilySelectedFaiProduct,
        setTemporarilySelectedFaiProduct,
        otisConfigurator,
    } = useHome();
    const searchParams = useSearchParams();

    const isFaiFirst = otisConfigurator.value === JOURNEYS.FAI;

    const selectedFaiProduct = isFaiFirst ? (_selectedFaiProduct ?? faiProducts[0]) : _selectedFaiProduct;
    const temporarilySelectedFaiProduct = _temporarilySelectedFaiProduct ?? faiProducts[0];

    const baseProduct = selectedFaiProduct ?? temporarilySelectedFaiProduct;
    const repeater = repeaters?.find((r) => r.gencode === baseProduct?.gencode_repeater);

    const planPrice = getPlanPrice(baseProduct);
    const isOtisVisible = baseProduct?.range_ng === RangesNewGeneration.VERY_FIT;

    const handleModal = (rangeNg: RangesNewGeneration) => {
        setDetailModalOffer(rangeNg);
    };

    const onChangeOffer = (product: FaiProduct) => {
        if (isFaiFirst) {
            setSelectedFaiProduct(product);
            setTemporarilySelectedFaiProduct(null);
        } else {
            setSelectedFaiProduct(null);
            setTemporarilySelectedFaiProduct(product);
        }
        if (selectedFaiOption && product.range_ng !== RangesNewGeneration.VERY_FIT) {
            setSelectedFaiOption('');
        }
    };

    const onAddOtisRepeater = () => {
        if (!repeater) {
            return;
        }
        if (selectedFaiProduct?.range_ng !== RangesNewGeneration.VERY_FIT) {
            setShouldShowAlert(true);
            setTimeout(() => {
                setShouldShowAlert(false);
            }, 5000);
            return;
        }
        if (selectedFaiOption) {
            setSelectedFaiOption('');
        } else {
            setSelectedFaiOption(repeater.gencode);
        }
    };

    useEffect(() => {
        if (!faiProducts[0]) {
            return;
        }

        const rangeNg = searchParams.get('range_ng');
        const commercialSeries = searchParams.get('commercial_series');
        const faiProductFromParams =
            rangeNg && commercialSeries
                ? faiProducts.find(
                      (offer) => offer.range_ng === rangeNg && offer.commercial_series === commercialSeries,
                  )
                : undefined;

        if (faiProductFromParams) {
            setSelectedFaiProduct(faiProductFromParams);
            setTemporarilySelectedFaiProduct(faiProductFromParams);
            return;
        }

        if (isFaiFirst) {
            setSelectedFaiProduct(faiProducts[0]);
            return;
        }

        setTemporarilySelectedFaiProduct(faiProducts[0]);
    }, [faiProducts]);

    const shouldShowByouAlert = !!(
        baseProduct?.alerte_client_byou &&
        baseProduct.title_alert &&
        baseProduct.text_alert
    );

    if (!faiProducts || faiProducts.length === 0) {
        return;
    }

    return (
        <>
            <div data-testid='bbox-container' className='pricing-table fai-pricing-table'>
                <div className='pricing-plan'>
                    <BAndyouPrice
                        bboxPrice={planPrice}
                        icon={
                            baseProduct?.picto?.public_url ? (
                                <div>
                                    <Image src={baseProduct.picto.public_url} alt='' height={24} width={64} />
                                </div>
                            ) : null
                        }
                    />
                    <Box className='is-radiusless-top'>
                        <Title level={TitleLevels.THREE} markup={TitleMarkup.H2}>
                            Quelle offre vous faut-il&nbsp;?
                        </Title>
                        <OfferRadioList
                            faiProducts={faiProducts}
                            selectedOffer={baseProduct}
                            onChangeOffer={onChangeOffer}
                        />
                        <Spacer size={SpacerSize.MEDIUM} />

                        <BboxCard selectedOffer={baseProduct} onClickDetails={handleModal} />
                    </Box>
                </div>
            </div>
            {shouldShowByouAlert && (
                <Alert
                    description={baseProduct.text_alert}
                    display
                    title={baseProduct.title_alert}
                    className='byou-alert'
                />
            )}
            {isOtisVisible && repeater && (
                <>
                    <Spacer size={SpacerSize.MEDIUM} />
                    <BboxOtisOption
                        onAddOtisRepeater={onAddOtisRepeater}
                        onClick={() => setIsOptionModalOpened(true)}
                        shouldShowAlert={shouldShowAlert}
                        repeater={repeater}
                    />
                </>
            )}
            <FaiDetailsModal
                faiOffer={baseProduct}
                onClose={() => setDetailModalOffer(undefined)}
                active={!!detailModalOffer}
            />
            {!!repeater && (
                <BboxOtisOptionModal
                    isRepeaterAdded={selectedFaiOption === repeater.gencode}
                    onAddOtisRepeater={onAddOtisRepeater}
                    onClose={() => setIsOptionModalOpened(false)}
                    active={isOptionModalOpened}
                    shouldShowAlert={shouldShowAlert}
                    repeater={repeater}
                />
            )}
        </>
    );
}
